import React, { useEffect, useRef, useState } from 'react'
import { Box, Dialog, Grid, SvgIcon, Typography } from '@material-ui/core'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import { CloseIconDialog } from '~/components/icons'
import { quickApplyModalStyled } from '~/features/quick-apply/quick-apply-component.style'
import { FIELD_MODE } from '~/common/constants'
import { careJobDetailStore } from '~/features/care-jobs/detail/care-job-detail.store'
import { useEffectOnce, useToggle } from 'react-use'
import BasicInformationForm from '~/features/care-activation/components/basic-information-form'
import { NotifyDialog } from '~/components/notify'
import { infoTabStore } from '~/features/care-activation/tabs/info/info-tab.store'
import { OtpDialog } from '~/components/otp-dialog'
import SectionBox from '~/components/section-box'
import ProfessionForm from '~/features/care-activation/components/profession-form'
import * as yup from 'yup'
import { Button } from '~/components/button'
import { careProfileStore } from '~/features/care-profile/care-profile.store'
import { careStore } from '~/companies/care/care.store'
import { quickApplyStore } from '~/features/quick-apply/quick-apply.store'
import { additionalInfoApplyDialogStore } from '~/components/additional-info-apply-dialog/additional-info-apply-dialog.store'
import { isEmpty, isNil } from 'lodash'
import { referenceTabStore } from '~/components/additional-info-apply-dialog/tabs/reference/reference-tab.store'
import { logClickEvent, logTrackingScreenEvent } from '~/common/tracking/event-client.tracking'
import { COMMON_EVENT_CLICK_TRACKING } from '~/common/constants/tracking.constant'
import { checkArrayItemIncludesAnyElement } from '~/components/apply-job-dialog/apply-job-dialog'
import { mapDisciplineSpecialty } from '~/components/apply-job-dialog/apply-job-dialog.helper'

const validateSchema = yup.object({
	firstName: yup
		.string()
		.trim()
		.required('Please enter your first name')
		.max(50, 'Maximum characters exceeded')
		.typeError('Please enter your first name')
		.min(2, 'Please ensure First name has at least 2 characters'),
	lastName: yup
		.string()
		.trim()
		.required('Please enter your last name')
		.max(50, 'Maximum characters exceeded')
		.typeError('Please enter your last name')
		.min(2, 'Please ensure Last name has at least 2 characters'),
	email: yup.string().trim().email('Please enter valid email').required('Please enter your email address'),
	phone: yup.string().trim().required('Please enter your phone').typeError('Please enter your phone').min(12, 'Invalid phone number format, please recheck'),
})

export const UpdateApplyJobDialog = observer(() => {
	const { showApplyUpdateDialog, setShowApplyUpdateDialog } = careJobDetailStore
	const setClose = () => setShowApplyUpdateDialog(false)

	const { t } = useTranslation()
	const [showVerifyPhone, toggleShowVerifyPhone] = useToggle(false)
	const [showVerifiedPhone, toggleShowVerifiedPhone] = useToggle(false)
	const [showPhoneOtp, toggleShowPhoneOtp] = useToggle(false)
	const [showEmailConfirmation, toggleEmailConfirmation] = useToggle(false)
	const formRef = useRef()
	const {
		phoneVerified,
		workerPhone,
		workerEmail,
		setWorkerNewEmail,
		infoInitialValues,
		professionInitialValues,
		fetchWorkerPhoneVerified,
		syncPersonalInfo,
		workerNewEmail,
		handleSubmitInfo,
		fetchWorkerBasicInfo,
		primarySpecialty,
		forceAddProfessions,
	} = infoTabStore
	const { getActiveFeature, workerBasicInfo } = careStore

	const { code: activeCode } = getActiveFeature() || {}
	const { currentJobID, currentJob } = quickApplyStore
	const { lookupSkillChecklistsByJob, setShowDialogMatchProfession } = additionalInfoApplyDialogStore
	const { resumes } = careProfileStore
	const { references } = referenceTabStore
	const [showErrorCodeValidateDispline, setShowErrorCodeValidateDispline] = useToggle(false)
	const [professionInfo, setProfessionInfo] = useState([])
	const displineJobId = currentJob.jobGeneral?.specialties.map((item) => item?.discipline?.id) || []
	const shouldOpenAdditionalInfo =
		(currentJob?.atsSource === 'symplr' && (careStore.symplrStatus === 'Prospect' || isNil(careStore.symplrStatus))) ||
		(currentJob?.atsSource === 'bullhorn' && (careStore.bullhornStatus === 'New Lead' || isNil(careStore.bullhornStatus)))
	const shouldShowAdditionalApplication = shouldOpenAdditionalInfo && (isEmpty(resumes) || references?.length < 2 || !isEmpty(lookupSkillChecklistsByJob))
	const checkIsLTS = currentJob?.isPerDiemLongTerm !== true && currentJob?.atsSource === 'symplr'

	const handleOnSubmit = async (values, { setSubmitting }) => {
		const emptySpecialty = isEmpty(primarySpecialty)
		if (!isNotDependDispline && checkIsLTS && !emptySpecialty) {
			setShowDialogMatchProfession(true)
			return
		} else if (
			emptySpecialty &&
			checkIsLTS &&
			!checkArrayItemIncludesAnyElement(
				displineJobId,
				professionInfo.map((item) => item.profession)
			)
		) {
			setShowDialogMatchProfession(true)
			return
		}

		if ((values.phone && (workerPhone ?? '')?.replace('+1', '') !== (values.phone ?? '')?.replace('+1', '')) || !phoneVerified) {
			toggleShowVerifyPhone(true)
			setSubmitting(false)

			return
		}

		if (values.email !== workerEmail) {
			setWorkerNewEmail(values.email)
			toggleEmailConfirmation(true)
			setSubmitting(false)

			return
		}
		if (emptySpecialty && !professionInfo.length) {
			await setShowErrorCodeValidateDispline(true)
			return
		}

		if (!isNil(primarySpecialty)) {
			await setShowErrorCodeValidateDispline(false)
		} else if (professionInfo.length && !emptySpecialty) {
			await setShowErrorCodeValidateDispline(false)
			return
		}

		if (emptySpecialty) await forceAddProfessions(professionInfo)
		await handleSubmitInfo(values)
		await fetchWorkerBasicInfo()
		await careStore.fetchWorkerBasicInfo()

		if (!shouldShowAdditionalApplication) {
			await careJobDetailStore
				.actionApplyJob({
					...values,
				})
				.then((res) => {
					careJobDetailStore.setJobItem(res)
				})

			if (activeCode === 'jobs') {
				await careJobDetailStore.fetchJob(quickApplyStore?.currentJobID)
			}

			await logClickEvent(COMMON_EVENT_CLICK_TRACKING.updateAndApplyBtn, {
				job_id: currentJobID,
				is_account_created: true, // true HERE
			})
		} else {
			await setShowApplyUpdateDialog(false)
			await additionalInfoApplyDialogStore.changeActiveStep(0)
			await quickApplyStore.setAutoAssignScl(false)
			await quickApplyStore.setShowAdditionInfo(true)
			additionalInfoApplyDialogStore.percentPerStep = {}
		}
	}

	const handleCancelUpdateNewEmail = (setFieldValue) => {
		toggleEmailConfirmation(false)
		setWorkerNewEmail(workerEmail)
		setFieldValue('email', workerEmail)
	}

	const handleCloseOtp = (values, setFieldValue, setSubmitting) => {
		toggleShowPhoneOtp(false)
		setFieldValue('phone', workerPhone)

		if (values.email !== workerEmail) {
			setWorkerNewEmail(values.email)
			toggleEmailConfirmation(true)
			setSubmitting(false)
		}
	}

	useEffectOnce(() => {
		showApplyUpdateDialog && logTrackingScreenEvent(COMMON_EVENT_CLICK_TRACKING.applyForThisJobUpdateApplyScreen)
	})

	useEffect(() => {
		if (showPhoneOtp) {
			return
		}
		const phoneInput = document.querySelector('.phone-input')
		if (phoneInput) {
			phoneInput.focus()
		}
	}, [showPhoneOtp])

	useEffect(() => {
		if (showVerifyPhone) {
			return
		}
		const phoneInput = document.querySelector('.phone-input')
		if (phoneInput) {
			phoneInput.focus()
		}
	}, [showVerifyPhone])

	const handleOnClose = () => {
		setClose(false)
		setProfessionInfo([])
		logClickEvent(COMMON_EVENT_CLICK_TRACKING.closeBtn, {
			job_id: currentJobID,
			is_account_created: true, // true HERE
		})
	}
	const dataMappingSpecialty = mapDisciplineSpecialty(workerBasicInfo?.workerDisciplineSpecialties, 'discipline', 'disciplineName')
	const workerDisplineIds = dataMappingSpecialty?.map((item) => item.value)
	const isNotDependDispline = checkArrayItemIncludesAnyElement(displineJobId, workerDisplineIds)

	const renderLabelSubmit = () => {
		if (currentJob?.atsSource === 'symplr') {
			return 'Update and request'
		} else {
			return 'Update and apply'
		}
	}
	return (
		<>
			{showApplyUpdateDialog && (
				<Dialog open={showApplyUpdateDialog} onClose={handleOnClose} css={quickApplyModalStyled}>
					<Box m={2}>
						<Grid container className="wrap-title">
							<Grid item xs={12} className="title">
								<Typography>Update your personal information</Typography>
							</Grid>

							<div
								onClick={handleOnClose}
								style={{
									position: 'absolute',
									top: 15,
									right: 15,
									cursor: 'pointer',
								}}
							>
								<SvgIcon component={CloseIconDialog} />
							</div>
						</Grid>

						<Formik innerRef={formRef} initialValues={infoInitialValues} onSubmit={handleOnSubmit} validationSchema={validateSchema}>
							{({ handleSubmit, isSubmitting, values, setFieldValue, setValues, errors, setSubmitting, resetForm }) => {
								useEffect(() => {
									syncPersonalInfo({ ...values })
								}, [values])
								return (
									<Form id="infoForm" onSubmit={handleSubmit}>
										<BasicInformationForm
											isApplyJob={true}
											mode={FIELD_MODE.edit}
											formValues={values}
											onVerifyPhone={() => (false ? toggleShowVerifiedPhone(true) : toggleShowVerifyPhone(true))}
											showPhoneOtp={showPhoneOtp}
										/>

										<Box mt={2}>
											<SectionBox header={t('PROFESSIONS_SPECIALTIES')}>
												<ProfessionForm
													updateOnly={true}
													setShowErrorCode={setShowErrorCodeValidateDispline}
													showErrorCode={showErrorCodeValidateDispline}
													mode={FIELD_MODE.edit}
													initialValues={professionInitialValues}
												/>
											</SectionBox>
										</Box>

										<NotifyDialog
											showButtonClose
											open={showVerifiedPhone}
											okText="Confirm"
											onOk={() => {
												toggleShowVerifiedPhone(false)
												toggleShowVerifyPhone(true)
											}}
											title={t('YOUR_PHONE_VERIFIED')}
											onCancel={() => {
												toggleShowVerifiedPhone(false)
											}}
											onClose={() => {
												toggleShowVerifiedPhone(false)
											}}
										>
											<Typography color="textSecondary">{t('$MESSAGES.CHANGE_PHONE_WARNING')}</Typography>
										</NotifyDialog>

										<NotifyDialog
											showButtonClose
											open={showVerifyPhone}
											okText="Confirm"
											onOk={() => {
												toggleShowPhoneOtp(true)
												toggleShowVerifyPhone(false)
												logClickEvent(COMMON_EVENT_CLICK_TRACKING.verifyBtn, {
													job_id: currentJobID,
													is_account_created: true, // true HERE
												})
											}}
											title={t('VERIFY_PHONE_NUMBER')}
											onCancel={() => {
												toggleShowVerifyPhone(false)

												if (values.email !== workerEmail) {
													setWorkerNewEmail(values.email)
													toggleEmailConfirmation(true)
													setSubmitting(false)
												}
												logClickEvent(COMMON_EVENT_CLICK_TRACKING.cancelBtn, {
													job_id: currentJobID,
													is_account_created: true, // true HERE
												})
											}}
											onClose={() => {
												toggleShowVerifyPhone(false)
											}}
										>
											<Box display="flex" flexDirection="column" gridRowGap={8}>
												<Typography color="textSecondary">
													Please confirm that <strong style={{ color: '#333335' }}>{values.phone}</strong> is your phone number to proceed verification.
												</Typography>
												<Typography color="textSecondary" onClick={() => toggleShowVerifyPhone(false)}>
													{t('$MESSAGES.CONFIRM_PHONE_NUMBER_ALT')}
													<span style={{ color: '#00AAE5', cursor: 'pointer' }}>update your phone number</span>
												</Typography>
											</Box>
										</NotifyDialog>

										<NotifyDialog
											open={showEmailConfirmation}
											okText="Confirm"
											onOk={async () => {
												await careProfileStore.handleSubmitInfoApply({ ...values, email: workerNewEmail }, resetForm)
												await toggleEmailConfirmation(false)
											}}
											title={t('$MESSAGES.UPDATE_EMAIL_CONFIRMATION_TITLE')}
											onCancel={() => {
												handleCancelUpdateNewEmail(setFieldValue)
											}}
											cancelText={t('$MESSAGES.KEEP_OLD_ONE')}
										>
											<Typography color="textSecondary">{t('$MESSAGES.UPDATE_EMAIL_CONFIRMATION_CONTENT')}</Typography>
										</NotifyDialog>

										<OtpDialog
											showDialog={showPhoneOtp}
											phoneNumber={values.phone}
											onClose={() => handleCloseOtp(values, setFieldValue, setSubmitting)}
											onCancel={() => handleCloseOtp(values, setFieldValue, setSubmitting)}
											onOk={() => {
												toggleShowPhoneOtp(false)
												void fetchWorkerPhoneVerified(setValues)

												if (values.email !== workerEmail) {
													setWorkerNewEmail(values.email)
													toggleEmailConfirmation(true)
													setSubmitting(false)
												}
											}}
										/>

										<Grid container spacing={2}>
											<Grid style={{ marginTop: 20, marginBottom: 20 }} item justify="space-between" container>
												<Grid item xs={6} style={{ paddingLeft: 4 }}>
													<Button onClick={handleOnClose} variant="outlined" type="button">
														Cancel
													</Button>
												</Grid>
												<Grid item xs={6} style={{ paddingLeft: 4 }}>
													<Button disabled={isSubmitting} loading={isSubmitting} type="submit">
														{renderLabelSubmit()}
													</Button>
												</Grid>
											</Grid>
										</Grid>
									</Form>
								)
							}}
						</Formik>
					</Box>
				</Dialog>
			)}
		</>
	)
})
